import React, { FC } from 'react'
import styled, { css } from 'styled-components'

import { ContentNode } from 'pages/Story/components/editorialRowHelpers'
import TileWrapper, {
  AspectRatio,
} from 'uiComponents/atoms/TileWrapper/TileWrapper'
import { mediaTabletAndDown } from 'uiComponents/constants/customMedia'

// As specified by design, not changable as of now
const DEFAULT_RATIO = '3:4'

export const SHIFTED_PADDING = 50
export const SMALL_TILE_WIDTH = 0.45
export const EVEN_TILE_WIDTH = 0.5
export const SPACE_BETWEEN_TILES = 4

const Wrapper = styled.div<{
  shift: boolean
}>`
  flex-grow: 1;
  width: 100%;

  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  padding: ${props => (props.shift ? SHIFTED_PADDING : 0)}px 0;

  > :first-child {
    margin-right: ${SPACE_BETWEEN_TILES / 2}px;
  }
  > :nth-child(2) {
    margin-left: ${SPACE_BETWEEN_TILES / 2}px;
  }

  @media ${mediaTabletAndDown} {
    flex-direction: column;
    padding: 0;

    > :first-child {
      width: 100%;
      margin-right: 0;
      margin-bottom: ${SPACE_BETWEEN_TILES / 2}px;
    }

    > :nth-child(2) {
      width: 100%;
      margin-left: 0;
      margin-top: ${SPACE_BETWEEN_TILES / 2}px;
    }
  }
`

type ShiftTo = 'bottom' | 'top'
type FlexStatus = 'flexEven' | 'flexBigger' | 'flexSmaller'

const StyledTileWrapper = styled(TileWrapper)<{
  applyRatio: boolean
  flexStatus: FlexStatus
  shiftTo?: ShiftTo
}>`
  flex-grow: 0;
  flex-shrink: 0;
  height: 100%;
  width: calc(
    ${(props): string => {
        switch (props.flexStatus) {
          case 'flexBigger':
            return `${(1 - SMALL_TILE_WIDTH) * 100}%`

          case 'flexSmaller':
            return `${SMALL_TILE_WIDTH * 100}%`

          default:
            return `${EVEN_TILE_WIDTH * 100}%`
        }
      }} - ${SPACE_BETWEEN_TILES / 2}px
  );

  transform: translateY(
    ${props => {
      if (!props.shiftTo) {
        return 0
      }

      return props.shiftTo === 'top' ? -50 : 50
    }}px
  );

  @media ${mediaTabletAndDown} {
    flex-grow: 1;
    flex-shrink: 1;
    flex-basis: 100%;

    transform: translateY(0);
  }

  ${props =>
    !props.applyRatio &&
    css`
      ::before {
        padding-bottom: 0;
      }

      > :first-child {
        position: relative;
      }
    `};
`

const getFlexStatus = ({
  content,
  contentOther,
}: {
  content: ContentNode
  contentOther: ContentNode
}): FlexStatus => {
  if (
    content.type === 'text' &&
    (contentOther.type === 'image' || contentOther.type === 'video')
  ) {
    return 'flexSmaller'
  }

  if (
    (content.type === 'image' || content.type === 'video') &&
    contentOther.type === 'text'
  ) {
    return 'flexBigger'
  }

  return 'flexEven'
}

interface DoubleTileProps {
  contentStart: ContentNode
  contentEnd: ContentNode
  ratio?: AspectRatio | null
  shift?: boolean
}

const DoubleTile: FC<DoubleTileProps> = ({
  contentEnd,
  contentStart,
  shift = false,
  ratio,
}) => {
  const startApplyRatio =
    contentStart.type === 'image' || contentStart.type === 'video'
  const endApplyRatio =
    contentEnd.type === 'image' || contentEnd.type === 'video'
  return (
    <Wrapper data-testid="EditorialDoubleTile" shift={shift}>
      <StyledTileWrapper
        applyRatio={startApplyRatio}
        flexStatus={getFlexStatus({
          content: contentStart,
          contentOther: contentEnd,
        })}
        ratio={ratio || DEFAULT_RATIO}
        shiftTo={shift ? 'top' : undefined}
      >
        {contentStart.node}
      </StyledTileWrapper>

      <StyledTileWrapper
        applyRatio={endApplyRatio}
        flexStatus={getFlexStatus({
          content: contentEnd,
          contentOther: contentStart,
        })}
        ratio={ratio || DEFAULT_RATIO}
        shiftTo={shift ? 'bottom' : undefined}
      >
        {contentEnd.node}
      </StyledTileWrapper>
    </Wrapper>
  )
}

export default DoubleTile
